import { motion } from "motion/react";
import { Database, Zap, Globe, Lock, TrendingUp, Users, BarChart3, Shield } from "lucide-react";

export function OmniNexusCards() {
  const features = [
    {
      icon: Database,
      title: "Omni-Nexus Central",
      description: "Hub unificado que conecta WooCommerce, WordPress, CRM e automações em uma arquitetura de dados centralizada",
      specs: [
        "MVC Architecture",
        "Repository Pattern",
        "Cache Inteligente 15min"
      ],
      color: "#00F5FF",
      glow: true
    },
    {
      icon: Users,
      title: "Identity Resolution V32",
      description: "Rastreie pessoas, não sessões. Sistema forense que identifica e unifica visitantes em múltiplas jornadas",
      specs: [
        "95% Accuracy Rate",
        "Cross-Device Tracking",
        "Lead Lifeline Timeline"
      ],
      color: "#00F5FF",
      glow: true
    },
    {
      icon: Zap,
      title: "Automação Inteligente",
      description: "Webhooks em tempo real que disparam ações automáticas baseadas no comportamento do usuário",
      specs: [
        "12 Trigger Types",
        "Zapier Integration",
        "Email Marketing Sync"
      ],
      color: "#10B981",
      glow: false
    },
    {
      icon: BarChart3,
      title: "Dashboard Comando",
      description: "Visualize métricas críticas em tempo real: receita, conversões, carrinhos ativos e origem de tráfego",
      specs: [
        "Real-Time Metrics",
        "Custom Widgets",
        "Export to CSV/JSON"
      ],
      color: "#00F5FF",
      glow: false
    },
    {
      icon: Shield,
      title: "Bot Killer System",
      description: "Elimine 99.7% de tráfego falso com detecção avançada de bots, crawlers e tráfego spam",
      specs: [
        "Machine Learning",
        "Behavioral Analysis",
        "Whitelist/Blacklist"
      ],
      color: "#FF4D00",
      glow: false
    },
    {
      icon: TrendingUp,
      title: "Revenue Recovery",
      description: "Recupere até 30% de carrinhos abandonados com automações de follow-up e triggers inteligentes",
      specs: [
        "Auto-Email Sequences",
        "Dynamic Coupons",
        "ROI Tracking"
      ],
      color: "#10B981",
      glow: false
    },
    {
      icon: Lock,
      title: "LGPD Compliant",
      description: "Rastreamento ético e transparente. Todos os dados ficam no seu servidor, zero vazamento para terceiros",
      specs: [
        "Self-Hosted Data",
        "Cookie Consent",
        "GDPR Ready"
      ],
      color: "#00F5FF",
      glow: false
    },
    {
      icon: Globe,
      title: "GPS Comportamental",
      description: "Mapeie a jornada completa do visitante: de onde veio, o que viu, onde hesitou e por que abandonou",
      specs: [
        "Geolocation Tracking",
        "Heatmap Integration",
        "Exit Intent Detection"
      ],
      color: "#00F5FF",
      glow: false
    }
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/95 to-[#0B0E14]" />

      {/* Hexagon pattern background */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="hexagons" x="0" y="0" width="100" height="87" patternUnits="userSpaceOnUse">
              <polygon
                points="50,0 93.3,25 93.3,62 50,87 6.7,62 6.7,25"
                fill="none"
                stroke="#00F5FF"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexagons)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00F5FF]/30 bg-[#00F5FF]/5 mb-6">
            <Database className="w-4 h-4 text-[#00F5FF]" />
            <span className="text-sm text-[#00F5FF] font-mono">ARCHITECTURE OVERVIEW</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Ecossistema Omni-Nexus
            <span className="block text-[#00F5FF]">Tudo Conectado</span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            8 módulos integrados em uma arquitetura de dados PhD-level. 
            Não é apenas tracking – é <span className="text-[#00F5FF] font-bold">inteligência de negócio</span>
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.05 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                {/* Glow effect (only for featured cards) */}
                {feature.glow && (
                  <div
                    className="absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"
                    style={{ backgroundColor: feature.color }}
                  />
                )}

                {/* Card */}
                <div
                  className="relative h-full p-6 rounded-xl border backdrop-blur-sm bg-gradient-to-br from-[#0B0E14]/90 to-[#0B0E14]/50 transition-all duration-300"
                  style={{ borderColor: `${feature.color}30` }}
                >
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{
                      backgroundColor: `${feature.color}15`,
                      border: `2px solid ${feature.color}40`
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: feature.color }} />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-white mb-3">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Specs */}
                  <div className="space-y-2">
                    {feature.specs.map((spec, specIdx) => (
                      <div key={specIdx} className="flex items-center gap-2">
                        <div
                          className="w-1 h-1 rounded-full flex-shrink-0"
                          style={{ backgroundColor: feature.color }}
                        />
                        <span className="text-xs text-gray-400 font-mono">{spec}</span>
                      </div>
                    ))}
                  </div>

                  {/* Corner accent */}
                  <div
                    className="absolute top-0 right-0 w-20 h-20 opacity-10 group-hover:opacity-20 transition-opacity"
                    style={{
                      background: `radial-gradient(circle at top right, ${feature.color} 0%, transparent 70%)`
                    }}
                  />

                  {/* Scan line effect */}
                  <motion.div
                    className="absolute inset-0 pointer-events-none rounded-xl overflow-hidden"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  >
                    <motion.div
                      className="absolute inset-x-0 h-px"
                      style={{ backgroundColor: feature.color, opacity: 0.3 }}
                      animate={{ y: [0, 300] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    />
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Connection Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative max-w-4xl mx-auto"
        >
          {/* Central Hub */}
          <div className="relative">
            {/* Connecting lines animation */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#00F5FF', stopOpacity: 0 }} />
                  <stop offset="50%" style={{ stopColor: '#00F5FF', stopOpacity: 0.5 }} />
                  <stop offset="100%" style={{ stopColor: '#00F5FF', stopOpacity: 0 }} />
                </linearGradient>
              </defs>
              {[0, 1, 2, 3, 4, 5].map((idx) => {
                const angle = (idx / 6) * 360;
                const x2 = 50 + Math.cos((angle * Math.PI) / 180) * 40;
                const y2 = 50 + Math.sin((angle * Math.PI) / 180) * 40;
                return (
                  <motion.line
                    key={idx}
                    x1="50%"
                    y1="50%"
                    x2={`${x2}%`}
                    y2={`${y2}%`}
                    stroke="url(#lineGradient)"
                    strokeWidth="2"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{
                      duration: 2,
                      delay: idx * 0.2,
                      repeat: Infinity,
                      repeatType: 'reverse'
                    }}
                  />
                );
              })}
            </svg>

            {/* Center Card */}
            <div className="relative z-10 max-w-md mx-auto">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                {/* Glow */}
                <div className="absolute -inset-2 rounded-2xl bg-[#00F5FF] opacity-20 blur-2xl" />
                
                {/* Card */}
                <div className="relative p-8 rounded-2xl border-2 border-[#00F5FF]/50 bg-gradient-to-br from-[#00F5FF]/10 to-[#0B0E14] backdrop-blur-sm">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00F5FF]/20 border-2 border-[#00F5FF] flex items-center justify-center">
                      <Database className="w-8 h-8 text-[#00F5FF]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      WPTT Core Engine
                    </h3>
                    <p className="text-sm text-gray-300 mb-4">
                      Arquitetura centralizada que orquestra todos os módulos em sincronia perfeita
                    </p>
                    <div className="flex items-center justify-center gap-2 text-[#10B981] text-sm font-mono">
                      <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                      Sistema Operacional
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-xl text-gray-300 mb-8">
            Não é apenas um plugin. É uma <span className="text-[#00F5FF] font-bold">transformação arquitetural</span> no 
            seu e-commerce.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="px-10 py-5 bg-[#FF4D00] hover:bg-[#FF4D00]/90 text-white text-lg rounded-lg font-bold transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FF4D00]/50">
              Implementar Agora
            </button>
            <button className="px-10 py-5 border-2 border-[#00F5FF] text-[#00F5FF] hover:bg-[#00F5FF]/10 text-lg rounded-lg font-bold transition-all">
              Ver Arquitetura Completa
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
